/// <amd-module name="TFS/Admin/Groups/Pivots/Permissions/Components/PermissionsPivotInitializer" />
import * as React from "react";
import { connect } from "react-redux";
import { PermissionTrace, PermissionValue, SubjectPermission, SubjectPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { generateRowId, getPermissionId, PermissionsValueDisplayNames } from "TFS/Admin/Common/Util/PermissionsHelper";
import { PermissionChangeStatus } from "TFS/Admin/Groups/Common/Constants";
import { IGroupsViewState, PermissionsGroup, SelectedPermissionChangeStatus } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as PageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { OrgMenuGroups, OrgPermissionsGroupKey, ProjectMenuGroups, ProjectPermissionsGroupKey } from "TFS/Admin/Groups/Pivots/Permissions/Constants";
import { PermissionsPivotActions } from "TFS/Admin/Groups/Pivots/Permissions/Redux/PermissionsPivotActions";
import * as PermissionsPivotSelectors from "TFS/Admin/Groups/Pivots/Permissions/Redux/PermissionsPivotSelectors";
import { GraphUser } from "VSS/Platform/Contracts/Graph";
import { VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Dropdown } from "VSSUI/Dropdown";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { Location } from "VSSUI/Utilities/Position";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";

interface IPermissionsPivotContainerProps {}

interface IPermissionsPivotInitializerOwnProps extends IPermissionsPivotContainerProps {
    selectedGroup?: PermissionsGroup;
    selectedUser?: GraphUser;
    subjectPermissions?: SubjectPermissions;
    selectedPermission?: SubjectPermission;
    permissionTraces: { [id: string]: PermissionTrace };
    messageCardText?: string;
    messageCardSeverity?: MessageCardSeverity;
    loading: boolean;
    selectedPermissionChangeStatus: SelectedPermissionChangeStatus;
    isProjectScope?: boolean;
}

interface IPermissionsPivotState {}

export type IPermissionsPivotInitializerProps = IPermissionsPivotInitializerOwnProps & typeof ActionsToDispatch;

export class PermissionPivotInitializer extends VssComponent<IPermissionsPivotInitializerProps, IPermissionsPivotState> {
    constructor(props: IPermissionsPivotInitializerProps) {
        super(props);
        this.props.initView(this._getSubjectDescriptor(), this._getSubjectName());
    }

    public componentDidUpdate(prevProps: IPermissionsPivotInitializerProps) {
        const { selectedGroup, selectedUser } = this.props;

        const prevDescriptor = prevProps.selectedGroup
            ? prevProps.selectedGroup.descriptor
            : prevProps.selectedUser
            ? prevProps.selectedUser.descriptor
            : null;

        const currDescriptor = selectedGroup ? selectedGroup.descriptor : selectedUser ? selectedUser.descriptor : null;

        if (currDescriptor && prevDescriptor && currDescriptor != prevDescriptor) {
            this.props.initView(this._getSubjectDescriptor(), this._getSubjectName());
        }
    }

    public componentWillUnmount() {
        this.props.disposeView();
    }

    public render(): JSX.Element {
        return (
            <>
                {this.props.loading ? (
                    <div className="flex-row flex-center justify-center flex-grow">
                        <Spinner size={SpinnerSize.large} label={Resources.Loading} />
                    </div>
                ) : (
                    <div>
                        {this.props.messageCardText && (
                            <MessageCard
                                className="permissions-card"
                                severity={this.props.messageCardSeverity}
                                onDismiss={() => this.props.onDismissMessageCard()}
                            >
                                {this.props.messageCardText}
                            </MessageCard>
                        )}
                        {this.props.subjectPermissions && (
                            <Card>
                                <div className="flex-column h-scroll-auto rhythm-vertical-4">
                                    <Observer itemProvider={this.props.subjectPermissions.subjectPermissions}>
                                        {(props: { itemProvider: SubjectPermission[] }) =>
                                            this.props.subjectPermissions
                                                ? this.props.subjectPermissions.userHasReadAccess
                                                    ? this._getPermissionsList(props.itemProvider, this.props.subjectPermissions.canEditPermissions)
                                                    : this._userDoesNotHaveReadPermissions()
                                                : this._getErrorExperience()
                                        }
                                    </Observer>
                                </div>
                            </Card>
                        )}
                    </div>
                )}
            </>
        );
    }

    private _getPermissionsList(permissions: SubjectPermission[], canEditPermissions: boolean): JSX.Element[] {
        const allRows: JSX.Element[] = [];

        var result: Map<string, SubjectPermission[]> = new Map();

        // Add the initial categories to the Map
        if (!this.props.isProjectScope) {
            OrgMenuGroups.forEach((obj) => {
                result.set(obj.key, []);
            });
        } else {
            ProjectMenuGroups.forEach((obj) => {
                result.set(obj.key, []);
            });
        }
        //Categorize all the permissions into each type
        permissions.forEach((item) => {
            const rowId = generateRowId(item);
            let permissionCategory = this.props.isProjectScope ? ProjectPermissionsGroupKey[rowId] : OrgPermissionsGroupKey[rowId];
            if (!permissionCategory) {
                this.props.logTelemetry(
                    GroupsComponent.PermissionsPivot,
                    "Non-categorized permission",
                    {
                        Id: rowId,
                        DisplayName: item.displayName
                    },
                    "No group key found"
                );

                result.get(Resources.Other)!.push(item);
            } else {
                if (result.has(permissionCategory)) {
                    result.get(permissionCategory)!.push(item);
                } else {
                    result.get(Resources.Other)!.push(item);
                    this.props.logTelemetry(
                        GroupsComponent.PermissionsPivot,
                        "Permission category not mapped",
                        {
                            Id: permissionCategory
                        },
                        "The permissions category received is not mapped"
                    );
                }
            }
        });

        result.forEach((value, key) => {
            if (value.length > 0) {
                allRows.push(this._renderHeaders(key));
                for (const item of value) {
                    allRows.push(this._renderItems(item, canEditPermissions));
                }
                allRows.push(this._renderSplitter(key));
            }
        });

        // Remove the last splitter
        allRows.pop();
        return allRows;
    }

    private _renderItems(permission: SubjectPermission, canEditPermissions: boolean): JSX.Element {
        const rowId = generateRowId(permission);
        return (
            <div className="flex-row" key={rowId}>
                <span className="margin-left-8 bolt-menuitem-cell-content permissions-bolt-menuitem-cell-text">{permission.displayName}</span>
                <span>
                    <Dropdown
                        className="permissions-dropdown"
                        ariaLabel={permission.displayName}
                        placeholder={permission.permissionDisplayString}
                        items={PermissionsValueDisplayNames}
                        onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<string>) =>
                            this.onSelect(event, item, permission, permission.permissionDisplayString)
                        }
                        disabled={!permission.canEdit || !canEditPermissions}
                        renderSelectedItems={(selection: IListSelection, items: IListBoxItem<string>[]) =>
                            this._renderSelectedItem(selection, items, permission.permissionDisplayString)
                        }
                    />
                </span>
                {this.props.subjectPermissions &&
                    this.props.subjectPermissions.userHasReadAccess &&
                    this.props.permissionTraces[rowId] &&
                    !this.props.permissionTraces[rowId].error &&
                    this._getInheritedPermissionInfo(permission, rowId)}
                {this._getPermissionState(rowId)}
            </div>
        );
    }

    private _renderHeaders(header: string): JSX.Element {
        return (
            <div key={header} className="font-weight-semibold bolt-menuitem-cell-content permissions-bolt-menuitem-cell-text">
                {header ? header : "Other"}
            </div>
        );
    }

    private _renderSplitter(key: string): JSX.Element {
        return <div key={key + "-splitter"} className="bolt-menuitem-divider-content"></div>;
    }

    private _userDoesNotHaveReadPermissions(): JSX.Element {
        return (
            <MessageCard className="permissions-card" severity={MessageCardSeverity.Error}>
                {Resources.PermissionsInsufficientReadPermissions}
            </MessageCard>
        );
    }

    private _renderSelectedItem(selection: IListSelection, items: IListBoxItem<string>[], permissionValue: string): string {
        selection.clear();

        return permissionValue;
    }

    private onSelect = (
        event: React.SyntheticEvent<HTMLElement>,
        item: IListBoxItem<string>,
        permission: SubjectPermission,
        placeholder?: string
    ) => {
        this.props.logTelemetry(GroupsComponent.PermissionsPivot, "Set permissions selection made", {});
        let selectedItem: string | undefined = item.text;

        if (permission && this.props.subjectPermissions && selectedItem) {
            if (placeholder && placeholder === selectedItem) {
                // This is a No-op
                return;
            }

            let permissionId: PermissionValue = getPermissionId(selectedItem);

            if (permission.inheritDenyOverride && permissionId == PermissionValue.Allow) {
                this.props.updatePermissionFailed(permission.namespaceId, permission.bit);
                return;
            }

            if (permissionId == PermissionValue.NotSet) {
                this.props.removePermission(
                    this.props.subjectPermissions.identityDescriptor!.toString(),
                    this._getSubjectDescriptor(),
                    permission.namespaceId,
                    permission.bit,
                    permission.token,
                    this.props.isProjectScope
                );
            } else {
                this.props.setPermission(
                    this.props.subjectPermissions.identityDescriptor,
                    {
                        namespaceId: permission.namespaceId,
                        token: permission.token,
                        permissionValue: permissionId,
                        permissionBit: permission.bit
                    },
                    this.props.isProjectScope
                );
            }
        }
    };

    private _getErrorExperience(): JSX.Element {
        return (
            <div className="empty-member flex-container">
                <div>
                    <VssIcon iconName="StatusCircleErrorX" iconType={VssIconType.fabric} className="empty-permissions-icon" />
                </div>
                <div className="empty-membersof-heading">{Resources.EmptyPermissionsHeading}</div>
                <div className="empty-membersof-body">{Resources.EmptyPermissionsBody}</div>
            </div>
        );
    }

    private _getSubjectDescriptor(): string {
        if (this.props.selectedGroup) {
            return this.props.selectedGroup.descriptor;
        } else if (this.props.selectedUser) {
            return this.props.selectedUser.descriptor;
        }

        return "";
    }

    private _getSubjectName(): string {
        if (this.props.selectedGroup) {
            return this.props.selectedGroup.displayName;
        } else if (this.props.selectedUser) {
            return this.props.selectedUser.displayName;
        }

        return "";
    }

    private _getInheritedPermissionInfo(permission: SubjectPermission, rowId: string): JSX.Element {
        if (permission.isPermissionInherited || permission.isPermissionGrantedBySystem) {
            return (
                <Link
                    tooltipProps={{
                        anchorOrigin: { horizontal: Location.end, vertical: Location.center },
                        renderContent: () => this._getTooltipContent(rowId, permission.isPermissionGrantedBySystem),
                        tooltipOrigin: { horizontal: Location.start, vertical: Location.center }
                    }}
                    className="permissions-inherited-info"
                    href={Resources.AzureDevOpsPermissionsLink}
                    disabled={!permission.isPermissionGrantedBySystem}
                    ariaLabel={Resources.SystemStorePermissionInfo}
                    tabIndex={0}
                >
                    <Icon iconName="Info" />
                </Link>
            );
        }

        return <div className="permissions-inherited-info" />;
    }

    private _getPermissionState(rowId: string): JSX.Element {
        if (this.props.selectedPermissionChangeStatus) {
            switch (this.props.selectedPermissionChangeStatus[rowId]) {
                case PermissionChangeStatus.Saved:
                    return (
                        <span role={"alert"}>
                            <Status
                                {...Statuses.Success}
                                key="saved"
                                size={StatusSize.l}
                                text={Resources.PermisionSetStatusSaved}
                                className="permissions-set-status"
                            />
                        </span>
                    );

                case PermissionChangeStatus.InProgress:
                    return (
                        <Status
                            {...Statuses.Running}
                            key="running"
                            size={StatusSize.l}
                            text={Resources.PermisionSetStatusInProgress}
                            className="permissions-set-status"
                        />
                    );

                case PermissionChangeStatus.Error:
                    return (
                        <span role={"alert"}>
                            <Status
                                {...Statuses.Failed}
                                key="failed"
                                size={StatusSize.l}
                                text={Resources.PermisionSetStatusError}
                                className="permissions-set-status"
                                tooltipContent={this._getPermissionChangeStatusErrorTooltipContent}
                            />
                        </span>
                    );

                case PermissionChangeStatus.None:
                default:
                    break;
            }
        }

        return <div />;
    }

    private _getTooltipContent = (id: string, isGrantedBySystem: boolean): JSX.Element => {
        if (isGrantedBySystem) {
            return (
                <div>
                    <p className="inheritance-type-description">{Resources.SystemStorePermissionInfo}</p>
                </div>
            );
        }

        return (
            <div>
                <p className="inheritance-type-description">{Resources.GroupInheritance}</p>
                {this.props.permissionTraces[id]!.affectingGroups.map((group, i) => (
                    <p key={i} className="inheritance-group-name">
                        {group.group.displayName}
                    </p>
                ))}
            </div>
        );
    };

    private _getPermissionChangeStatusErrorTooltipContent():React.ReactNode {
        return (<p>{Resources.InheritedDenyTrumpsAllowText}<a href={Resources.LearnMoreLink} target="_blank"> {Resources.LearnMoreText}</a></p>)
    }
}

//Update component props whenever the store's state changes
function mapStateToProps(state: IGroupsViewState, providedProps: IPermissionsPivotContainerProps): Partial<IPermissionsPivotInitializerOwnProps> {
    return {
        ...providedProps,
        selectedGroup: PageSelectors.getSelectedGroup(state),
        selectedUser: PageSelectors.getSelectedUser(state),
        isProjectScope: PermissionsPivotSelectors.isProjectScope(state),
        subjectPermissions: PermissionsPivotSelectors.getSubjectPermissions(state),
        permissionTraces: PermissionsPivotSelectors.getPermissionTraces(state),
        selectedPermission: PermissionsPivotSelectors.getSelectedPermission(state),
        messageCardText: PermissionsPivotSelectors.getMessageCardText(state),
        messageCardSeverity: PermissionsPivotSelectors.getMessageCardSeverity(state),
        loading: PermissionsPivotSelectors.getLoading(state),
        selectedPermissionChangeStatus: PermissionsPivotSelectors.getSelectedPermissionChangeStatus(state)
    };
}

//Hook up action creators to reducer
const ActionsToDispatch = {
    initView: PermissionsPivotActions.initView,
    disposeView: PermissionsPivotActions.disposeView,
    setPermission: PermissionsPivotActions.setPermission,
    removePermission: PermissionsPivotActions.removePermission,
    selectPermission: PermissionsPivotActions.selectPermission,
    updatePermissionFailed: PermissionsPivotActions.updatePermissionFailed,
    picklistActive: PermissionsPivotActions.pickListActive,
    logTelemetry: GroupsViewActions.logTelemetry,
    onDismissMessageCard: PermissionsPivotActions.onDismissMessageCard
};

export const ConnectedPermissionPivotInitializer = connect(mapStateToProps, ActionsToDispatch)(PermissionPivotInitializer);
